<script lang="ts">
import { computed, defineComponent, h, inject, unref } from 'vue';

import { classNames } from '@components/utils';

import { makeLazyProps, makeReadonlyProps, useRender } from '../../composable';
import { CdsIcon } from '../CdsIcon';

import { CdsExpandablePanelSymbol, CdsExpandablePanelsGroupSymbol } from './CdsExpandablePanels.vue';
import { makeExpandablePanelTitleProps } from './composable';

export const componentName = 'CdsExpandablePanelTitle';

export default defineComponent({
  name: componentName,
  props: {
    ...makeExpandablePanelTitleProps(),
    ...makeReadonlyProps(),
    ...makeLazyProps()
  },
  setup(props, { slots, expose }) {
    const panel = inject(CdsExpandablePanelsGroupSymbol);
    const panelsInject = inject(CdsExpandablePanelSymbol);

    const isReadOnly = computed(() => panelsInject?.readOnly || props.readonly);

    if (!panel) throw new Error('[CDS] cds-expandable-panel-title needs to be placed inside cds-expandable-panel');

    const slotProps = computed(() => ({
      collapseIcon: props.collapseIcon,
      expanded: unref(panel.isSelected),
      disabled: unref(panel.disabled),
      readOnly: unref(isReadOnly),
      expandIcon: props.expandIcon
    }));

    useRender(() =>
      h(
        'button',
        {
          class: classNames('cds-expandable-panel__title', {
            'cds-expandable-panel__title--selected': unref(panel.isSelected),
            'cds-expandable-panel__title--disabled': unref(panel.disabled),
            'cds-expandable-panel__title--readonly': unref(isReadOnly)
          }),
          onClick: !unref(isReadOnly) ? panel.toggle : undefined,
          tabindex: unref(panel.disabled) ? -1 : undefined,
          ariaExpanded: unref(panel.isSelected),
          disabled: unref(panel.disabled),
          type: 'button'
        },
        [
          slots.default?.(unref(slotProps)),
          !props.hideActions &&
            h(
              'span',
              {
                class: 'cds-expandable-panel__title-icon'
              },
              [
                slots.action
                  ? slots.action(unref(slotProps))
                  : h(CdsIcon, {
                      class: 'cds-expandable-panel__title-icon',
                      name: unref(panel.isSelected) ? props.collapseIcon : props.expandIcon
                    })
              ]
            )
        ]
      )
    );

    expose({
      ...panel
    });

    return {};
  }
});
</script>
